import {
  class_of_use_enum,
  country_enum,
  country_state_enum,
  efficiency_class_enum,
  energy_certificate_type_enum,
  energy_source_type_enum,
  energy_system_type_enum,
  nl_building_group_enum,
  type_of_use_enum,
} from '@predium/enums';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';

// Primary-energy upper bounds per efficiency class [kWh/m²·yr]. `null` marks countries that have
// no single national ladder — the UK (CO2/rating-based) and Belgium (per-region ladders, see
// BelgiumEfficiencyClassLookup) — getEfficiencyClassByEnergy throws for them instead of banding
// against fake thresholds.
// The AAAA/AAAAA/AAAAAA slots (A+++/A++++/A+++++) exist only for the NL ladders (BLU-3255) —
// every other ladder pins them to -Infinity so the band-walk can never return them (0 would
// swallow zero-energy buildings, which belong to the country's own top class).
const EfficiencyClasses: Record<country_enum, Record<efficiency_class_enum, number> | null> = {
  DE: {
    AAAAAA: -Infinity,
    AAAAA: -Infinity,
    AAAA: -Infinity,
    AAA: 0,
    AA: 30,
    A: 50,
    B: 75,
    C: 100,
    D: 130,
    E: 160,
    F: 200,
    G: 250,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  AT: {
    AAAAAA: -Infinity,
    AAAAA: -Infinity,
    AAAA: -Infinity,
    AAA: 60,
    AA: 70,
    A: 80,
    B: 160,
    C: 220,
    D: 280,
    E: 340,
    F: 400,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  PL: {
    AAAAAA: -Infinity,
    AAAAA: -Infinity,
    AAAA: -Infinity,
    AAA: 0,
    AA: 0,
    A: 63,
    B: 157,
    C: 250,
    D: 344,
    E: 438,
    F: 531,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  FR: {
    AAAAAA: -Infinity,
    AAAAA: -Infinity,
    AAAA: -Infinity,
    AAA: 0,
    AA: 0,
    A: 70,
    B: 110,
    C: 180,
    D: 250,
    E: 330,
    F: 420,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  // UK EPC efficiency classes are CO2/rating-based and commercial-only — there is no primary-energy
  // ladder. getEfficiencyClass routes the UK to getEfficiencyClassUKCommercial before the energy
  // lookup; calling getEfficiencyClassByEnergy with the UK throws. BLU-2940.
  UK: null,
  // Belgian EPC/PEB labels are regional (Flanders/Wallonia/Brussels each publish their own
  // scale, split residential vs. non-residential). getEfficiencyClass routes Belgium to
  // getEfficiencyClassBelgium; calling getEfficiencyClassByEnergy with BE throws. BLU-3012.
  BE: null,
  // Czechia does NOT use fixed thresholds — it classifies via a building-specific reference
  // value (see getCzechEfficiencyClass / CZ_THRESHOLDS, BLU-3029). This entry only exists to
  // satisfy the Record<country_enum, …> type; it is never read for CZ.
  CZ: {
    AAAAAA: 0,
    AAAAA: 0,
    AAAA: 0,
    AAA: 0,
    AA: 0,
    A: 0,
    B: 0,
    C: 0,
    D: 0,
    E: 0,
    F: 0,
    G: 0,
    H: 0,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  // NL efficiency classes are building-group-dependent — residential uses a single ladder, while
  // non-residential has 8 different ladders that may be area-weighted for mixed commercial use.
  // getEfficiencyClass routes NL to getNlEfficiencyClass before the energy lookup. BLU-2988.
  NL: null,
};

const HeatingDemandAustriaLookup: Record<efficiency_class_enum, number> = {
  AAAAAA: -Infinity,
  AAAAA: -Infinity,
  AAAA: -Infinity,
  AAA: 10,
  AA: 15,
  A: 25,
  B: 50,
  C: 100,
  D: 150,
  E: 200,
  F: 250,
  G: Infinity,
  H: Infinity,
  NOT_APPLICABLE: 0,
  UNKNOWN: 0,
};

//this is to make sure they are evaluated in the right order, even when the order in the enum should change
// Use Record<> to make sure that we get a compile error when adding new values to the enum
const EfficiencyClassOrderRecord: Record<efficiency_class_enum, null> = {
  [efficiency_class_enum.AAAAAA]: null,
  [efficiency_class_enum.AAAAA]: null,
  [efficiency_class_enum.AAAA]: null,
  [efficiency_class_enum.AAA]: null,
  [efficiency_class_enum.AA]: null,
  [efficiency_class_enum.A]: null,
  [efficiency_class_enum.B]: null,
  [efficiency_class_enum.C]: null,
  [efficiency_class_enum.D]: null,
  [efficiency_class_enum.E]: null,
  [efficiency_class_enum.F]: null,
  [efficiency_class_enum.G]: null,
  [efficiency_class_enum.H]: null,
  [efficiency_class_enum.NOT_APPLICABLE]: null,
  [efficiency_class_enum.UNKNOWN]: null,
};

export const EfficiencyClassOrder: efficiency_class_enum[] = Object.keys(
  EfficiencyClassOrderRecord,
) as efficiency_class_enum[];

export function compareEfficiencyClass(a: efficiency_class_enum, b: efficiency_class_enum): number {
  return EfficiencyClassOrder.indexOf(a) - EfficiencyClassOrder.indexOf(b);
}

// A+++–A+++++ exist only on the Dutch ladders — consumers use this to hide the bands for
// portfolios that cannot produce them.
export const NL_ONLY_EFFICIENCY_CLASSES = [
  efficiency_class_enum.AAAA,
  efficiency_class_enum.AAAAA,
  efficiency_class_enum.AAAAAA,
] as const;

export function getEfficiencyClass({
  country,
  countryState,
  classOfUse,
  energySystems,
  heatingDemand,
  btiParams,
  totalPrimaryEnergy,
  co2PerM2,
  energyRatingScore,
  referenceValueClass,
  typeOfUseAreas,
  ebfArea,
  energyProduced,
}: {
  country: country_enum;
  // Required (nullish allowed): UK Scotland and all Belgian classifications depend on it —
  // omitting it silently degrades those to UNKNOWN, so call sites must state it explicitly.
  countryState: country_state_enum | null | undefined;
  classOfUse: class_of_use_enum | string;
  energySystems: {
    energy_system_type: energy_system_type_enum;
    energy_system_consumer_routes: {
      energy_final: number;
      energy_source_type: energy_source_type_enum;
      energy_primary: number;
    }[];
  }[];
  heatingDemand: number | null;
  btiParams?: {
    certificateType?: energy_certificate_type_enum;
    gegReferenceValue?: number;
    enevYear?: number;
    referenceValueHeatingHotWater?: number;
    referenceValueElectricity?: number;
  };
  totalPrimaryEnergy?: number;
  // UK Scotland bands this CO2 intensity (kgCO2/m²·yr); France's CO2 class also uses it.
  co2PerM2?: number | null;
  // UK rest-of-UK bands the EPC's printed Energy Performance Asset Rating directly.
  energyRatingScore?: number | null;
  // Czechia: building-specific non-renewable primary energy reference value (E_R). The class is
  // derived from the ratio of the building's primary energy to this reference (BLU-3029).
  referenceValueClass?: number;
  // NL commercial: area-weighted type-of-use distribution for threshold blending (BLU-2988).
  typeOfUseAreas?: Partial<Record<type_of_use_enum, number>>;
  // EBF [m²] of the class of use being classified. Flanders splits non-residential labeling on it:
  // units > 500 m² are banded by renewable share instead of primary energy (BLU-3241).
  // Omitting it for a Flemish non-residential building throws — pass null when the area is
  // genuinely unavailable (falls back to the kNR ladder).
  ebfArea?: number | null;
  // PV production [kWh] (producer routes with a SOLAR source) — producers are not part of
  // energySystems, which carries consumer routes only. Feeds the Flemish renewable share.
  // Omitting it for a Flemish non-residential building throws — pass null when PV production
  // cannot be part of the input (counts as 0 in the share).
  energyProduced?: number | null;
}): efficiency_class_enum {
  // UK EPC efficiency classes are defined only for commercial buildings.
  if (country === country_enum.UK) {
    if (classOfUse !== class_of_use_enum.COMMERCIAL) {
      return efficiency_class_enum.NOT_APPLICABLE;
    }
    return getEfficiencyClassUKCommercial({ countryState, co2PerM2, energyRatingScore });
  }
  if (country === country_enum.CZ) {
    const czechPrimaryEnergy =
      totalPrimaryEnergy ??
      sumBy(energySystems, (system) => sumBy(system.energy_system_consumer_routes, (route) => route.energy_primary));
    return getCzechEfficiencyClass(czechPrimaryEnergy, referenceValueClass);
  }

  if (country === country_enum.NL) {
    const nlPrimaryEnergy =
      totalPrimaryEnergy ??
      sumBy(energySystems, (system) => sumBy(system.energy_system_consumer_routes, (route) => route.energy_primary));
    return getNlEfficiencyClass(nlPrimaryEnergy, classOfUse, typeOfUseAreas);
  }

  // For German commercial buildings with BTI parameters, use BTI classification
  if (classOfUse === class_of_use_enum.COMMERCIAL && country === country_enum.DE && btiParams) {
    // Use provided totalPrimaryEnergy if available, otherwise calculate from energy systems
    const primaryEnergyForBTI =
      totalPrimaryEnergy ??
      sumBy(energySystems, (system) => sumBy(system.energy_system_consumer_routes, (route) => route.energy_primary));

    const btiResult = calculateBTIClassification(
      btiParams.certificateType,
      primaryEnergyForBTI,
      btiParams.gegReferenceValue,
      btiParams.enevYear,
      btiParams.referenceValueHeatingHotWater,
      btiParams.referenceValueElectricity,
    );

    if (btiResult) {
      return btiResult.class;
    }
  }

  // For German commercial buildings without BTI parameters, return NOT_APPLICABLE
  if (classOfUse === class_of_use_enum.COMMERCIAL && country === country_enum.DE) {
    return efficiency_class_enum.NOT_APPLICABLE;
  }

  // For Polish commercial buildings, return NOT_APPLICABLE
  if (classOfUse === class_of_use_enum.COMMERCIAL && country === country_enum.PL) {
    return efficiency_class_enum.NOT_APPLICABLE;
  }

  // For Austrian commercial buildings, continue to regular lookup logic (will return AAA for low energy values)

  //calculate the energy that is used to look up the efficiency class based on country
  let lookupEnergy = 0;
  switch (country) {
    case country_enum.DE:
      //use final energy heating and hot water for the lookup
      lookupEnergy = sumBy(
        energySystems.filter(
          (energySystem) =>
            energySystem.energy_system_type === energy_system_type_enum.HEATING ||
            energySystem.energy_system_type === energy_system_type_enum.HOT_WATER,
        ),
        (energySystem) => sumBy(energySystem.energy_system_consumer_routes, (route) => route.energy_final),
      );
      break;
    case country_enum.AT:
      if (heatingDemand) {
        for (const efficiencyClass of EfficiencyClassOrder) {
          if (heatingDemand <= HeatingDemandAustriaLookup[efficiencyClass]) {
            return efficiencyClass;
          }
        }
      }
      //use primary energy for the lookup
      lookupEnergy = sumBy(energySystems, (system) =>
        sumBy(system.energy_system_consumer_routes, (route) => route.energy_primary),
      );
      break;
    case country_enum.PL:
      //use primary energy for the lookup
      lookupEnergy = sumBy(energySystems, (system) =>
        sumBy(system.energy_system_consumer_routes, (route) => route.energy_primary),
      );
      break;
    case country_enum.FR:
      //use primary energy for the lookup (DPE standard)
      lookupEnergy = sumBy(energySystems, (system) =>
        sumBy(system.energy_system_consumer_routes, (route) => route.energy_primary),
      );
      break;
    // country_enum.NL is handled above via getNlEfficiencyClass (building-group-dependent).
    case country_enum.BE:
      // Belgian EPC/PEB labels are primary-energy based in all three regions, but each region
      // has its own scale split by residential/non-residential use — see getEfficiencyClassBelgium.
      // Large Flemish non-residential is the exception: it bands the renewable share instead.
      return getEfficiencyClassBelgium({
        primaryEnergy: sumBy(energySystems, (system) =>
          sumBy(system.energy_system_consumer_routes, (route) => route.energy_primary),
        ),
        countryState,
        classOfUse,
        ebfArea,
        // undefined marks a call site that never wired the PV input — getEfficiencyClassBelgium
        // throws on it for Flemish non-residential instead of silently misbanding the share.
        renewableShare:
          energyProduced === undefined ? undefined : getRenewableEnergyShare({ energySystems, energyProduced }),
      });
    // country_enum.UK is handled above via getEfficiencyClassUKCommercial (CO2-based, commercial-only).

    default:
      throw new Error(`Country ${country} not supported`);
  }

  //use the calculated lookup energy to... look up the efficiency class
  return getEfficiencyClassByEnergy(lookupEnergy, country);
}

export function getEfficiencyClassByEnergy(lookupEnergy: number, country: country_enum) {
  const thresholds = EfficiencyClasses[country];
  if (!thresholds) {
    throw new Error(
      `${country} efficiency classes are not banded by a single national energy ladder — use getEfficiencyClass, which routes to the country-specific classification`,
    );
  }
  for (const efficiencyClass of EfficiencyClassOrder) {
    if (lookupEnergy <= thresholds[efficiencyClass]) {
      return efficiencyClass;
    }
  }
  return efficiency_class_enum.UNKNOWN;
}

/**
 * UK EPC efficiency classes (commercial only).
 *
 * Unlike DE/AT/PL/FR, UK EPC bands are driven by CO2 emissions (kgCO2/m²·yr) rather than primary
 * energy, and are only defined for commercial buildings. The banding rule depends on the UK nation:
 *
 * - Scotland (UK_SCOTLAND): band the CO2 intensity, rounded to the nearest whole number, directly.
 * - Rest of the UK (England regions, Wales, Northern Ireland): band the EPC's printed Energy
 *   Performance Asset Rating directly — that rating already IS the efficiency score the A–G bands
 *   divide (score = 50 · BER / SER = rating), so no CO2 value is needed on this path.
 *
 * The EPC "A+" band has no dedicated efficiency_class_enum member, so it maps to `AA` (the member
 * directly above `A`). `AAA` and `H` are unused for the UK. A UK-specific label variant renders
 * `AA` as "A+".
 *
 * Source: National Calculation Methodology (NCM) modelling guide, 2021 edition. BLU-2940.
 */
// Scotland: upper bounds on the (rounded) CO2 intensity [kgCO2/m²·yr]. A+ = < 0.
const UKCommercialScotlandLookup: Record<efficiency_class_enum, number> = {
  AAAAAA: -Infinity, // unused for the UK
  AAAAA: -Infinity, // unused for the UK
  AAAA: -Infinity, // unused for the UK
  AAA: -Infinity, // unused for the UK
  AA: -1, // A+  (rounded CO2 < 0)
  A: 15,
  B: 30,
  C: 45,
  D: 60,
  E: 80,
  F: 100,
  G: Infinity,
  H: Infinity, // unused for the UK
  NOT_APPLICABLE: -Infinity,
  UNKNOWN: -Infinity,
};

// Rest of UK: upper bounds on the printed Energy Performance Asset Rating. A+ = < 0 (unused in practice).
const UKCommercialRestOfUKLookup: Record<efficiency_class_enum, number> = {
  AAAAAA: -Infinity, // unused for the UK
  AAAAA: -Infinity, // unused for the UK
  AAAA: -Infinity, // unused for the UK
  AAA: -Infinity, // unused for the UK
  AA: -1,
  A: 25,
  B: 50,
  C: 75,
  D: 100,
  E: 125,
  F: 150,
  G: Infinity,
  H: Infinity, // unused for the UK
  NOT_APPLICABLE: -Infinity,
  UNKNOWN: -Infinity,
};

function bandByUpperBounds(value: number, lookup: Record<efficiency_class_enum, number>): efficiency_class_enum {
  for (const efficiencyClass of EfficiencyClassOrder) {
    if (value <= lookup[efficiencyClass]) {
      return efficiencyClass;
    }
  }
  return efficiency_class_enum.UNKNOWN;
}

/**
 * UK Standard Emission Rate (SER), derived from the certificate's BER (CO2 per m²) and the printed
 * Energy Performance Asset Rating score: `SER = 50 * co2PerM2 / ratingScore`. It is materialized on
 * building_model.standard_emission_rate during building-model recalculation (BLU-2940).
 * Returns null when either input is missing or the score is 0.
 */
export function deriveUkStandardEmissionRate(
  co2PerM2: number | null | undefined,
  ratingScore: number | null | undefined,
): number | null {
  if (co2PerM2 === undefined || co2PerM2 === null || !ratingScore) {
    return null;
  }
  return (50 * co2PerM2) / ratingScore;
}

/**
 * UK commercial EPC efficiency class. The two nations band on different inputs:
 * - Scotland bands the certificate's CO2 intensity (kgCO2/m²·yr), rounded to the nearest integer.
 * - England regions, Wales, Northern Ireland band the EPC's printed Energy Performance Asset Rating
 *   directly — that rating already IS the score the A–G bands divide (score = 50·BER/SER = rating),
 *   so no CO2 or SER is needed at this point.
 *
 * Returns UNKNOWN when the required input for the nation is missing.
 */
export function getEfficiencyClassUKCommercial({
  countryState,
  co2PerM2,
  energyRatingScore,
}: {
  countryState: country_state_enum | null | undefined;
  // Scotland only: the CO2 intensity that gets banded directly.
  co2PerM2?: number | null;
  // Rest of UK only: the EPC's printed asset rating, banded directly.
  energyRatingScore?: number | null;
}): efficiency_class_enum {
  if (countryState === country_state_enum.UK_SCOTLAND) {
    // Scotland bands the CO2 intensity rounded to the nearest whole number (e.g. 30.3 → B, 30.7 → C).
    if (co2PerM2 === undefined || co2PerM2 === null) {
      return efficiency_class_enum.UNKNOWN;
    }
    return bandByUpperBounds(Math.round(co2PerM2), UKCommercialScotlandLookup);
  }

  // England regions, Wales, Northern Ireland: band the printed asset rating directly.
  if (energyRatingScore === undefined || energyRatingScore === null) {
    return efficiency_class_enum.UNKNOWN;
  }
  return bandByUpperBounds(energyRatingScore, UKCommercialRestOfUKLookup);
}

type BelgiumRegion = country_state_enum.BE_BRUSSELS | country_state_enum.BE_FLANDERS | country_state_enum.BE_WALLONIA;

const BelgiumEfficiencyClassLookup: Record<
  BelgiumRegion,
  {
    residential: Record<efficiency_class_enum, number>;
    // `null` = the region publishes no non-residential labels (Wallonia) → NOT_APPLICABLE.
    nonResidential: Record<efficiency_class_enum, number> | null;
  }
> = {
  [country_state_enum.BE_BRUSSELS]: {
    // A ≤45, B 46–95, C 96–150, D 151–210, E 211–275, F 276–345, G >345
    residential: {
      AAAAAA: -Infinity, // unused for Belgium
      AAAAA: -Infinity, // unused for Belgium
      AAAA: -Infinity, // unused for Belgium
      AAA: -Infinity, // unused for Brussels
      AA: -Infinity, // unused for Brussels
      A: 45,
      B: 95,
      C: 150,
      D: 210,
      E: 275,
      F: 345,
      G: Infinity,
      H: Infinity, // unused for Belgium
      NOT_APPLICABLE: -Infinity,
      UNKNOWN: -Infinity,
    },
    // A ≤62, B 63–155, C 156–248, D 249–341, E 342–434, F 435–527, G >527
    nonResidential: {
      AAAAAA: -Infinity, // unused for Belgium
      AAAAA: -Infinity, // unused for Belgium
      AAAA: -Infinity, // unused for Belgium
      AAA: -Infinity, // unused for Brussels
      AA: -Infinity, // unused for Brussels
      A: 62,
      B: 155,
      C: 248,
      D: 341,
      E: 434,
      F: 527,
      G: Infinity,
      H: Infinity, // unused for Belgium
      NOT_APPLICABLE: -Infinity,
      UNKNOWN: -Infinity,
    },
  },
  [country_state_enum.BE_WALLONIA]: {
    // A++ ≤0, A+ 1–45, A 46–85, B 86–170, C 171–255, D 256–340, E 341–425, F 426–510, G >510
    residential: {
      AAAAAA: -Infinity, // unused for Belgium
      AAAAA: -Infinity, // unused for Belgium
      AAAA: -Infinity, // unused for Belgium
      AAA: 0, // A++
      AA: 45, // A+
      A: 85,
      B: 170,
      C: 255,
      D: 340,
      E: 425,
      F: 510,
      G: Infinity,
      H: Infinity, // unused for Belgium
      NOT_APPLICABLE: -Infinity,
      UNKNOWN: -Infinity,
    },
    nonResidential: null,
  },
  [country_state_enum.BE_FLANDERS]: {
    // A+ <0, A 0–100, B 101–200, C 201–300, D 301–400, E 401–500, F >500 (scale ends at F)
    residential: {
      AAAAAA: -Infinity, // unused for Belgium
      AAAAA: -Infinity, // unused for Belgium
      AAAA: -Infinity, // unused for Belgium
      AAA: -Infinity, // unused for Flanders
      // A+ is strictly < 0: -Number.MIN_VALUE is the largest negative double, so `value <= bound`
      // matches every negative value and nothing else (-Number.EPSILON would misband tiny negatives).
      AA: -Number.MIN_VALUE,
      A: 100,
      B: 200,
      C: 300,
      D: 400,
      E: 500,
      F: Infinity, // the Flemish residential scale has no G
      G: Infinity,
      H: Infinity, // unused for Belgium
      NOT_APPLICABLE: -Infinity,
      UNKNOWN: -Infinity,
    },
    // Small non-residential (EPC kNR): A+ ≤0, A 1–160, B 161–265, C 266–365, D 366–470,
    // E 471–575, F >575 (scale ends at F). Applied to all non-res — see the doc comment above.
    nonResidential: {
      AAAAAA: -Infinity, // unused for Belgium
      AAAAA: -Infinity, // unused for Belgium
      AAAA: -Infinity, // unused for Belgium
      AAA: -Infinity, // unused for Flanders
      AA: 0, // A+
      A: 160,
      B: 265,
      C: 365,
      D: 470,
      E: 575,
      F: Infinity, // the Flemish kNR scale has no G
      G: Infinity,
      H: Infinity, // unused for Belgium
      NOT_APPLICABLE: -Infinity,
      UNKNOWN: -Infinity,
    },
  },
};

function isBelgiumRegion(countryState: country_state_enum | null | undefined): countryState is BelgiumRegion {
  return (
    countryState === country_state_enum.BE_BRUSSELS ||
    countryState === country_state_enum.BE_FLANDERS ||
    countryState === country_state_enum.BE_WALLONIA
  );
}

export const FLANDERS_LARGE_NON_RESIDENTIAL_EBF_THRESHOLD_M2 = 500;

const FLANDERS_NR_RENEWABLE_SHARE_THRESHOLDS: Array<{ minShare: number; class: efficiency_class_enum }> = [
  { minShare: 100, class: efficiency_class_enum.A },
  { minShare: 50, class: efficiency_class_enum.B },
  { minShare: 25, class: efficiency_class_enum.C },
  { minShare: 10, class: efficiency_class_enum.D },
  { minShare: 5, class: efficiency_class_enum.E },
  { minShare: Number.MIN_VALUE, class: efficiency_class_enum.F },
  { minShare: -Infinity, class: efficiency_class_enum.G },
];

const RENEWABLE_ENERGY_SOURCES: energy_source_type_enum[] = [
  energy_source_type_enum.SOLAR,
  energy_source_type_enum.ELECTRICITY_GREEN,
  energy_source_type_enum.DISTRICT_HEATING_CHP_RENEWABLE,
  energy_source_type_enum.DISTRICT_HEATING_PLANTS_RENEWABLE,
];

export function getRenewableEnergyShare({
  energySystems,
  energyProduced,
}: {
  energySystems: {
    energy_system_consumer_routes: {
      energy_final: number;
      energy_source_type: energy_source_type_enum;
    }[];
  }[];
  energyProduced?: number | null;
}): number | null {
  const consumerRoutes = energySystems.flatMap((system) => system.energy_system_consumer_routes);
  const totalFinalEnergy = sumBy(consumerRoutes, (route) => route.energy_final);
  if (totalFinalEnergy <= 0) {
    return null;
  }
  const renewableFinalEnergy = sumBy(
    consumerRoutes.filter((route) => RENEWABLE_ENERGY_SOURCES.includes(route.energy_source_type)),
    (route) => route.energy_final,
  );
  return (((energyProduced ?? 0) + renewableFinalEnergy) / totalFinalEnergy) * 100;
}

export function getEfficiencyClassBelgium({
  primaryEnergy,
  countryState,
  classOfUse,
  ebfArea,
  renewableShare,
}: {
  primaryEnergy: number;
  countryState: country_state_enum | null | undefined;
  classOfUse: class_of_use_enum | string;
  ebfArea?: number | null;
  renewableShare?: number | null;
}): efficiency_class_enum {
  if (!isBelgiumRegion(countryState)) {
    return efficiency_class_enum.UNKNOWN;
  }
  const isFlemishNonResidential =
    countryState === country_state_enum.BE_FLANDERS && classOfUse !== class_of_use_enum.RESIDENTIAL;
  if (isFlemishNonResidential) {
    if (ebfArea === undefined || renewableShare === undefined) {
      throw new Error(
        'Flemish non-residential efficiency classes need ebfArea and energyProduced (BLU-3241) — pass null when a value is unavailable',
      );
    }
    if (!isNil(ebfArea) && ebfArea > FLANDERS_LARGE_NON_RESIDENTIAL_EBF_THRESHOLD_M2) {
      if (renewableShare === null) {
        return efficiency_class_enum.UNKNOWN;
      }
      const threshold = FLANDERS_NR_RENEWABLE_SHARE_THRESHOLDS.find(({ minShare }) => renewableShare >= minShare);
      return threshold?.class ?? efficiency_class_enum.UNKNOWN;
    }
  }
  const ladder =
    classOfUse === class_of_use_enum.RESIDENTIAL
      ? BelgiumEfficiencyClassLookup[countryState].residential
      : BelgiumEfficiencyClassLookup[countryState].nonResidential;
  if (!ladder) {
    return efficiency_class_enum.NOT_APPLICABLE;
  }
  return bandByUpperBounds(primaryEnergy, ladder);
}

/**
 * Czech efficiency-class thresholds (BLU-3029).
 * The class is the band whose upper bound the ratio (building non-renewable primary energy /
 * reference value E_R) does not exceed: A ≤0.8, B ≤1.2, C ≤1.6, D ≤2.3, E ≤3.0, F ≤3.7, G >3.7.
 */
const CZ_THRESHOLDS: Array<{ maxRatio: number; class: efficiency_class_enum }> = [
  { maxRatio: 0.8, class: efficiency_class_enum.A },
  { maxRatio: 1.2, class: efficiency_class_enum.B },
  { maxRatio: 1.6, class: efficiency_class_enum.C },
  { maxRatio: 2.3, class: efficiency_class_enum.D },
  { maxRatio: 3.0, class: efficiency_class_enum.E },
  { maxRatio: 3.7, class: efficiency_class_enum.F },
  { maxRatio: Infinity, class: efficiency_class_enum.G },
];

/**
 * Czech efficiency class. Requires a building-specific reference value (E_R); without a valid
 * reference or a valid primary-energy value the building cannot be classified (UNKNOWN).
 */
function getCzechEfficiencyClass(buildingPrimaryEnergy: number, referenceValueClass?: number): efficiency_class_enum {
  if (
    !referenceValueClass ||
    referenceValueClass <= 0 ||
    !Number.isFinite(buildingPrimaryEnergy) ||
    buildingPrimaryEnergy < 0
  ) {
    return efficiency_class_enum.UNKNOWN;
  }

  const ratio = buildingPrimaryEnergy / referenceValueClass;
  for (const threshold of CZ_THRESHOLDS) {
    if (ratio <= threshold.maxRatio) {
      return threshold.class;
    }
  }
  return efficiency_class_enum.G;
}

/**
 * NL efficiency classes (BLU-2988).
 *
 * Residential buildings use a single primary-energy ladder. Non-residential buildings use one of
 * 8 building-group ladders, area-weighted when a commercial building has multiple types of use.
 */
type NlNonResidentialGroup = Exclude<nl_building_group_enum, nl_building_group_enum.RESIDENTIAL>;

export function getNlBuildingGroup(typeOfUse: type_of_use_enum): nl_building_group_enum {
  switch (typeOfUse) {
    case type_of_use_enum.MFH:
    case type_of_use_enum.SFH:
    case type_of_use_enum.COMMUNAL_ACCOMMODATION:
    case type_of_use_enum.DORM:
      return nl_building_group_enum.RESIDENTIAL;

    case type_of_use_enum.EXHIBITION_BUILDING:
    case type_of_use_enum.LIBRARY:
    case type_of_use_enum.TRANSPORT_TERMINAL:
    case type_of_use_enum.FIRE_DEPARTMENT:
    case type_of_use_enum.POLICE_STATION:
    case type_of_use_enum.ELDERLY_DAYCARE_CENTER:
    case type_of_use_enum.LEISURE_AND_COMMUNITY_FACILITY:
    case type_of_use_enum.YOUTH_CENTER:
      return nl_building_group_enum.BIJEENKOMST;

    case type_of_use_enum.EDUCATION_INSTITUTION:
    case type_of_use_enum.LECTURE_AND_TEACHING_BUILDING:
    case type_of_use_enum.UNIVERSITY_AND_RESEARCH_INSTITUTION:
      return nl_building_group_enum.ONDERWIJS;

    case type_of_use_enum.DOCTOR_OFFICE:
    case type_of_use_enum.REHABILITATION_CENTER:
      return nl_building_group_enum.ZORG_ZONDER;

    case type_of_use_enum.MEDICAL_AND_NURSING_FACILITY:
      return nl_building_group_enum.ZORG_MET;

    case type_of_use_enum.RETAIL_NON_FOOD:
    case type_of_use_enum.SHOPPING_CENTER:
    case type_of_use_enum.CAFETERIA:
    case type_of_use_enum.RESTAURANT:
    case type_of_use_enum.RETAIL_FOOD:
      return nl_building_group_enum.WINKEL;

    case type_of_use_enum.GYM:
    case type_of_use_enum.OUTDOOR_SPORT_FACILITY:
    case type_of_use_enum.RIDING_AND_ICE_SPORT_HALL:
    case type_of_use_enum.SPORT_AND_LEISURE_BUILDING:
    case type_of_use_enum.EVENT_HALL:
    case type_of_use_enum.EXHIBITION_HALL:
    case type_of_use_enum.LOGISTIC_STORAGE_OPERATION_BUILDING:
    case type_of_use_enum.PARKING_GARAGE:
    case type_of_use_enum.VEHICLE_FLEET_FACILITY:
    case type_of_use_enum.VEHICLE_STORAGE_HALL:
      return nl_building_group_enum.SPORT;

    case type_of_use_enum.HOTEL:
      return nl_building_group_enum.LOGIES;

    default:
      return nl_building_group_enum.KANTOOR;
  }
}

const NlResidentialThresholds: Record<efficiency_class_enum, number> = {
  AAAAAA: -Infinity, // the residential scale tops out at A++++
  AAAAA: 0, // A++++
  AAAA: 50, // A+++
  AAA: 75, // A++
  AA: 105, // A+
  A: 160,
  B: 190,
  C: 250,
  D: 290,
  E: 335,
  F: 380,
  G: Infinity,
  H: Infinity,
  NOT_APPLICABLE: 0,
  UNKNOWN: 0,
};

const NlNonResidentialThresholds: Record<NlNonResidentialGroup, Record<efficiency_class_enum, number>> = {
  [nl_building_group_enum.KANTOOR]: {
    AAAAAA: 0,
    AAAAA: 40,
    AAAA: 80,
    AAA: 120,
    AA: 160,
    A: 180,
    B: 200,
    C: 225,
    D: 250,
    E: 275,
    F: 300,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  [nl_building_group_enum.BIJEENKOMST]: {
    AAAAAA: 0,
    AAAAA: 50,
    AAAA: 100,
    AAA: 150,
    AA: 200,
    A: 230,
    B: 255,
    C: 285,
    D: 320,
    E: 355,
    F: 385,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  [nl_building_group_enum.ONDERWIJS]: {
    AAAAAA: 0,
    AAAAA: 50,
    AAAA: 100,
    AAA: 150,
    AA: 200,
    A: 235,
    B: 260,
    C: 295,
    D: 330,
    E: 360,
    F: 395,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  [nl_building_group_enum.ZORG_ZONDER]: {
    AAAAAA: 0,
    AAAAA: 45,
    AAAA: 90,
    AAA: 135,
    AA: 180,
    A: 210,
    B: 230,
    C: 260,
    D: 295,
    E: 325,
    F: 355,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  [nl_building_group_enum.ZORG_MET]: {
    AAAAAA: 0,
    AAAAA: 90,
    AAAA: 180,
    AAA: 270,
    AA: 360,
    A: 430,
    B: 470,
    C: 530,
    D: 595,
    E: 655,
    F: 715,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  [nl_building_group_enum.WINKEL]: {
    AAAAAA: 0,
    AAAAA: 60,
    AAAA: 120,
    AAA: 180,
    AA: 240,
    A: 285,
    B: 315,
    C: 355,
    D: 395,
    E: 435,
    F: 475,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  [nl_building_group_enum.SPORT]: {
    AAAAAA: 0,
    AAAAA: 35,
    AAAA: 70,
    AAA: 105,
    AA: 140,
    A: 155,
    B: 170,
    C: 195,
    D: 215,
    E: 240,
    F: 260,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
  [nl_building_group_enum.LOGIES]: {
    AAAAAA: 0,
    AAAAA: 50,
    AAAA: 100,
    AAA: 150,
    AA: 200,
    A: 230,
    B: 255,
    C: 285,
    D: 320,
    E: 355,
    F: 385,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: 0,
    UNKNOWN: 0,
  },
};

export function blendNlThresholds(
  typeOfUseAreas: Partial<Record<type_of_use_enum, number>>,
): Record<efficiency_class_enum, number> {
  const entries = Object.entries(typeOfUseAreas) as [type_of_use_enum, number][];
  if (entries.length === 0) {
    return NlNonResidentialThresholds[nl_building_group_enum.KANTOOR];
  }

  const groups = entries.map(([tou, area]) => ({
    group: getNlBuildingGroup(tou),
    area,
  }));

  const nonResidentialGroups = groups.filter(
    (g): g is { group: NlNonResidentialGroup; area: number } => g.group !== nl_building_group_enum.RESIDENTIAL,
  );

  if (nonResidentialGroups.length === 0) {
    return NlNonResidentialThresholds[nl_building_group_enum.KANTOOR];
  }

  if (nonResidentialGroups.length === 1) {
    return NlNonResidentialThresholds[nonResidentialGroups[0].group];
  }

  const allSameGroup = nonResidentialGroups.every((g) => g.group === nonResidentialGroups[0].group);
  if (allSameGroup) {
    return NlNonResidentialThresholds[nonResidentialGroups[0].group];
  }

  const totalArea = sumBy(nonResidentialGroups, (g) => g.area);
  const blended = {} as Record<efficiency_class_enum, number>;

  for (const efficiencyClass of EfficiencyClassOrder) {
    const weightedSum = nonResidentialGroups.reduce((acc, { group, area }) => {
      const threshold = NlNonResidentialThresholds[group][efficiencyClass];
      if (!Number.isFinite(threshold)) {
        return Infinity;
      }
      if (!Number.isFinite(acc)) {
        return Infinity;
      }
      return acc + (area / totalArea) * threshold;
    }, 0);
    blended[efficiencyClass] = weightedSum;
  }

  return blended;
}

function getNlEfficiencyClass(
  primaryEnergy: number,
  classOfUse: class_of_use_enum | string,
  typeOfUseAreas?: Partial<Record<type_of_use_enum, number>>,
): efficiency_class_enum {
  const thresholds =
    classOfUse === class_of_use_enum.RESIDENTIAL ? NlResidentialThresholds : blendNlThresholds(typeOfUseAreas ?? {});

  for (const efficiencyClass of EfficiencyClassOrder) {
    if (primaryEnergy <= thresholds[efficiencyClass]) {
      return efficiencyClass;
    }
  }
  return efficiency_class_enum.UNKNOWN;
}

/**
 * BTI classification thresholds
 */
const BTI_THRESHOLDS: Array<{ maxRatio: number; class: efficiency_class_enum }> = [
  { maxRatio: 0.35, class: efficiency_class_enum.A },
  { maxRatio: 0.5, class: efficiency_class_enum.B },
  { maxRatio: 0.71, class: efficiency_class_enum.C },
  { maxRatio: 1.0, class: efficiency_class_enum.D },
  { maxRatio: 1.41, class: efficiency_class_enum.E },
  { maxRatio: 2.0, class: efficiency_class_enum.F },
  { maxRatio: Infinity, class: efficiency_class_enum.G },
];

/**
 * EnEV standards mapping by year
 */
const ENEV_STANDARDS: Array<{ maxYear: number; standards: { peHeating: number; peElectricity: number } }> = [
  { maxYear: 2009, standards: { peHeating: 1.1, peElectricity: 2.7 } },
  { maxYear: 2013, standards: { peHeating: 1.1, peElectricity: 2.4 } },
  { maxYear: 2016, standards: { peHeating: 1.1, peElectricity: 1.8 } },
  { maxYear: Infinity, standards: { peHeating: 1.1, peElectricity: 1.8 } },
];

/**
 * Calculate BTI classification for German non-residential buildings
 */
function calculateBTIClassification(
  certificateType?: energy_certificate_type_enum,
  actualPrimaryEnergy?: number,
  gegReferenceValue?: number,
  enevYear?: number,
  referenceValueHeatingHotWater?: number,
  referenceValueElectricity?: number,
): { ratio: number; class: efficiency_class_enum } | null {
  if (!actualPrimaryEnergy || actualPrimaryEnergy <= 0 || !certificateType) {
    return null;
  }

  // DEMAND certificates
  if (certificateType === energy_certificate_type_enum.DEMAND) {
    if (!gegReferenceValue || gegReferenceValue <= 0) {
      return null;
    }

    const ratio = actualPrimaryEnergy / gegReferenceValue;
    const btiClass = getBTIClass(ratio);

    return {
      ratio,
      class: btiClass,
    };
  }

  // CONSUMPTION certificates
  if (certificateType === energy_certificate_type_enum.CONSUMPTION) {
    if (!enevYear || !referenceValueHeatingHotWater || !referenceValueElectricity) {
      return null;
    }

    const enevStandards = getEnEVStandards(enevYear);
    const referenceTotal =
      referenceValueHeatingHotWater * enevStandards.peHeating + referenceValueElectricity * enevStandards.peElectricity;
    const ratio = actualPrimaryEnergy / referenceTotal;
    const btiClass = getBTIClass(ratio);

    return {
      ratio,
      class: btiClass,
    };
  }

  return null;
}

/**
 * Get EnEV standards based on year
 */
function getEnEVStandards(enevYear: number): { peHeating: number; peElectricity: number } {
  const standard = ENEV_STANDARDS.find((s) => enevYear <= s.maxYear);
  return standard?.standards || ENEV_STANDARDS[ENEV_STANDARDS.length - 1].standards;
}

/**
 * Get BTI class based on ratio
 */
function getBTIClass(ratio: number): efficiency_class_enum {
  for (const threshold of BTI_THRESHOLDS) {
    if (ratio <= threshold.maxRatio) {
      return threshold.class;
    }
  }
  return efficiency_class_enum.G;
}

export function averageEfficiencyClass(
  areaByEfficiencyClass: {
    area: number;
    efficiencyClass: efficiency_class_enum;
  }[],
) {
  const orderedEfficiencyClasses = EfficiencyClassOrder.map((efficiencyClass) => {
    const matching = areaByEfficiencyClass.filter((c) => c.efficiencyClass === efficiencyClass);
    if (matching.length === 0) {
      return {
        area: 0,
        efficiencyClass,
      };
    }
    return {
      area: sumBy(matching, ({ area }) => area),
      efficiencyClass,
    };
  });

  //remove unknown efficiency class and commercial buildings with efficiency class NOT_APPLICABLE
  const cleanClasses = orderedEfficiencyClasses.filter(
    (efficiencyClassBracket) =>
      efficiencyClassBracket.efficiencyClass !== efficiency_class_enum.UNKNOWN &&
      efficiencyClassBracket.efficiencyClass !== efficiency_class_enum.NOT_APPLICABLE,
  );

  const totalArea = sumBy(cleanClasses, ({ area }) => area);

  if (totalArea === 0) return efficiency_class_enum.NOT_APPLICABLE; //for commercial only portfolios

  const averageWeightedEfficiencyClassIndex = cleanClasses.reduce((acc, efficiencyClassBracket, index) => {
    const efficiencyClassIndex = index + 1; //+1 because otherwise class 0 has no effect
    return acc + (efficiencyClassBracket.area / totalArea) * efficiencyClassIndex;
  }, -1); //start with -1 because otherwise class 0 has no effect

  return EfficiencyClassOrder[Math.round(averageWeightedEfficiencyClassIndex)]; //round to get the closest class and get enum value for it
}

type Co2GroupFR = 'RESIDENTIAL' | 'ADMINISTRATION_EDUCATION' | 'CONTINUOUS_USE' | 'OTHER';

function getCo2GroupFR(typeOfUse: type_of_use_enum): Co2GroupFR {
  switch (typeOfUse) {
    // Residential — CRREM: MFH
    case type_of_use_enum.MFH:
    case type_of_use_enum.SFH:
    case type_of_use_enum.COMMUNAL_ACCOMMODATION:
    case type_of_use_enum.DORM:
      return 'RESIDENTIAL';

    // Administration & Education — CRREM: OFFICE
    case type_of_use_enum.ADMINISTRATIVE_BUILDING:
    case type_of_use_enum.EDUCATION_INSTITUTION:
    case type_of_use_enum.FIRE_DEPARTMENT:
    case type_of_use_enum.PARKING_GARAGE:
    case type_of_use_enum.POLICE_STATION:
    case type_of_use_enum.UNIVERSITY_AND_RESEARCH_INSTITUTION:
    case type_of_use_enum.UTILITY_AND_INFRASTRUCTURE_BUILDING:
    case type_of_use_enum.VEHICLE_FLEET_FACILITY:
    case type_of_use_enum.YOUTH_CENTER:
      return 'ADMINISTRATION_EDUCATION';

    // Continuous Use — CRREM: HEALTH_FACILITY + HOTEL + LODGING_LEISURE_RECREATION + DISTRIBUTION_WAREHOUSE_COLD
    case type_of_use_enum.COLD_WAREHOUSE:
    case type_of_use_enum.DATA_CENTER:
    case type_of_use_enum.DOCTOR_OFFICE:
    case type_of_use_enum.ELDERLY_DAYCARE_CENTER:
    case type_of_use_enum.EVENT_HALL:
    case type_of_use_enum.EXHIBITION_BUILDING:
    case type_of_use_enum.GYM:
    case type_of_use_enum.HOTEL:
    case type_of_use_enum.LABORATORY_AND_RESEARCH_BUILDING:
    case type_of_use_enum.LECTURE_AND_TEACHING_BUILDING:
    case type_of_use_enum.LEISURE_AND_COMMUNITY_FACILITY:
    case type_of_use_enum.LIBRARY:
    case type_of_use_enum.MEDICAL_AND_NURSING_FACILITY:
    case type_of_use_enum.OUTDOOR_SPORT_FACILITY:
    case type_of_use_enum.REHABILITATION_CENTER:
    case type_of_use_enum.RIDING_AND_ICE_SPORT_HALL:
    case type_of_use_enum.SPORT_AND_LEISURE_BUILDING:
      return 'CONTINUOUS_USE';

    // Other — CRREM: RETAIL + SHOPPING_CENTER + RETAIL_WAREHOUSE + DISTRIBUTION_WAREHOUSE_WARM
    default:
      return 'OTHER';
  }
}

const Co2EfficiencyClassFRLookup: Record<Co2GroupFR, Record<efficiency_class_enum, number>> = {
  RESIDENTIAL: {
    AAAAAA: -1,
    AAAAA: -1,
    AAAA: -1,
    AAA: -1,
    AA: -1,
    A: 5,
    B: 10,
    C: 20,
    D: 35,
    E: 55,
    F: 80,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: -1,
    UNKNOWN: -1,
  },
  ADMINISTRATION_EDUCATION: {
    AAAAAA: -1,
    AAAAA: -1,
    AAAA: -1,
    AAA: -1,
    AA: -1,
    A: 5,
    B: 15,
    C: 30,
    D: 60,
    E: 100,
    F: 145,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: -1,
    UNKNOWN: -1,
  },
  CONTINUOUS_USE: {
    AAAAAA: -1,
    AAAAA: -1,
    AAAA: -1,
    AAA: -1,
    AA: -1,
    A: 12,
    B: 30,
    C: 65,
    D: 110,
    E: 160,
    F: 220,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: -1,
    UNKNOWN: -1,
  },
  OTHER: {
    AAAAAA: -1,
    AAAAA: -1,
    AAAA: -1,
    AAA: -1,
    AA: -1,
    A: 3,
    B: 10,
    C: 25,
    D: 45,
    E: 70,
    F: 95,
    G: Infinity,
    H: Infinity,
    NOT_APPLICABLE: -1,
    UNKNOWN: -1,
  },
};

export function getCo2EfficiencyClassFrance(co2PerM2: number, typeOfUse: type_of_use_enum): efficiency_class_enum {
  const group = getCo2GroupFR(typeOfUse);
  const thresholds = Co2EfficiencyClassFRLookup[group];
  for (const efficiencyClass of EfficiencyClassOrder) {
    if (co2PerM2 <= thresholds[efficiencyClass]) {
      return efficiencyClass;
    }
  }
  return efficiency_class_enum.UNKNOWN;
}
